import { Divider } from '@mui/material'
import Stack from '@mui/material/Stack'
import { format } from 'date-fns'
import debounce from 'lodash-es/debounce'
import isEmpty from 'lodash-es/isEmpty'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getLastPosition, getNextPosition, getPreviousPosition } from '../../../../chess/gameTree'
import { Color } from '../../../../chess/types'
import { Dialogs, useDialogs } from '../../../../sharedComponents/src/Contexts/DialogsContext'
import { useGlobalContext } from '../../../../sharedComponents/src/Contexts/GlobalContext'
import { GroupNames } from '../../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import CircularButton from '../../../analysisView/components/analyisControls/CircularButton'
import AnalysisIcon from '../../icons/Analysis'
import BackIcon from '../../icons/Back'
import HintIcon from '../../icons/Hint'
import NextIcon from '../../icons/Next'
import SolutionIcon from '../../icons/Solution'
import TagIcon from '../../icons/Tag'
import PuzzleTags from './PuzzleTags'

const PuzzlePracticeControls: React.FC = () => {
    const userData = useStoreState((state) => state.userData.userData)
    const puzzle = useStoreState((state) => state.puzzlePracticeView.puzzle)
    const myColor = useStoreState((state) => state.puzzlePracticeView.myColor)
    const gameTree = useStoreState((state) => state.puzzlePracticeView.gameTree)
    const availableHints = useStoreState((state) => state.puzzlePracticeView.availableHints)
    const showHintLimit = useStoreState((state) => state.puzzlePracticeView.showHintLimit)
    const ftueOptions = useStoreState((state) => state.puzzlePracticeView.ftueOptions)
    const customArrows = useStoreState((state) => state.puzzlePracticeView.customArrows)
    const hintDescription = useStoreState((state) => state.puzzlePracticeView.hintDescription)
    const showBackdrop = useStoreState((state) => state.puzzlePracticeView.showBackdrop)
    const puzzleState = useStoreState((state) => state.puzzlePracticeView.puzzleState)
    const currentPosition = useStoreState((state) => state.puzzlePracticeView.currentPosition)
    const showSolution = useStoreState((state) => state.puzzlePracticeView.showSolution)
    const notations = useStoreState((state) => state.puzzlePracticeView.notations)

    const setPGNHeader = useStoreActions((state) => state.setPGNHeader)
    const setAnalysisGameTree = useStoreActions((state) => state.analysisMode.setGameTree)
    const handleHint = useStoreActions((state) => state.puzzlePracticeView.handleHint)
    const autoMove = useStoreActions((state) => state.puzzlePracticeView.autoMove)
    const setCurrentPosition = useStoreActions((state) => state.puzzlePracticeView.setCurrentPosition)

    const { registerDialog } = useDialogs()
    const { userType } = useGlobalContext()
    const { isMobile } = useScreenSize()
    const navigate = useNavigate()

    const hasNextMoves = currentPosition.nextMoveIds.length > 0
    const hasPrevMoves = currentPosition.previousMoveId !== undefined
    const disableByState = puzzleState !== null
    const isInitialized = !Boolean(notations.length)
    const disableButtonsForFtue = !isEmpty(ftueOptions)
    const disableByMoveList = useMemo(() => currentPosition.id !== getLastPosition(gameTree).id, [currentPosition.id])

    const handleTags = () => {
        registerDialog(Dialogs.PUZZLE_TAGS, <PuzzleTags />)
    }

    const handleAnalysis = () => {
        if (puzzle) {
            setPGNHeader({
                Event: 'Puzzle from chessclub.com',
                Site: 'Internet Chess Club http://chessclub.com/',
                Date: format(new Date(), 'yyyy.MM.dd'),
                Time: format(new Date(), 'HH:mm:ss'),
                FEN: puzzle.fen,
                Round: '-',
                White: myColor === Color.White ? userData?.user_name || '' : '',
                WhiteAvatarUrl: (myColor === Color.White && userData?.avatar_url) || '',
                WhiteTitle: (myColor === Color.White && userData?.title) || '',
                WhiteCountry: (myColor === Color.White && userData?.country) || '',
                Black: myColor === Color.Black ? userData?.user_name || '' : '',
                BlackAvatarUrl: (myColor === Color.Black && userData?.avatar_url) || '',
                BlackTitle: (myColor === Color.Black && userData?.title) || '',
                BlackCountry: (myColor === Color.Black && userData?.country) || '',
                Result: '*',
            })

            setAnalysisGameTree(gameTree)
            navigate('/analysis')
        }
    }

    const handleShowSolution = () => {
        autoMove(true)
    }

    const debouncedHandleHint = useMemo(() => {
        return debounce(
            () => {
                handleHint(userType)
            },
            500,
            { leading: true, trailing: false },
        )
    }, [handleHint, userType])

    const onBackMove = () => {
        const position = getPreviousPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }

    const onNextMove = () => {
        const position = getNextPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }

    const hintFtue = showBackdrop && ftueOptions?.highlightHint
    const hint2Ftue = showBackdrop && ftueOptions?.highlightSecondHint
    const hintText =
        userType === GroupNames.SUBSCRIBED && availableHints === 0
            ? 'Earn more from Daily Bonus'
            : hintDescription !== ''
              ? hintDescription
              : undefined

    return (
        <Stack direction="row" gap="24px">
            <CircularButton
                onClick={handleTags}
                title="Themes"
                icon={<TagIcon />}
                removeBorder
                disabled={isEmpty(puzzle) || showSolution || disableButtonsForFtue}
            />
            <CircularButton
                onClick={handleAnalysis}
                title="Analysis"
                icon={<AnalysisIcon />}
                removeBorder
                disabled={isEmpty(puzzle) || disableButtonsForFtue}
            />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'text.primary', my: 0.75 }} />
            {puzzleState === true && isMobile ? (
                <CircularButton
                    onClick={onBackMove}
                    title="Back"
                    disabled={!hasPrevMoves}
                    removeBorder
                    icon={<BackIcon />}
                />
            ) : (
                <CircularButton
                    onClick={handleShowSolution}
                    title="Solution"
                    icon={<SolutionIcon />}
                    disabled={
                        disableByState ||
                        isEmpty(puzzle) ||
                        showSolution ||
                        isInitialized ||
                        disableButtonsForFtue ||
                        disableByMoveList
                    }
                />
            )}
            {puzzleState === true && isMobile ? (
                <CircularButton
                    onClick={onNextMove}
                    title="Next"
                    disabled={!hasNextMoves}
                    removeBorder
                    icon={<NextIcon />}
                />
            ) : (
                <CircularButton
                    onClick={debouncedHandleHint}
                    title="Hint"
                    description={hintText}
                    icon={<HintIcon />}
                    badge={availableHints >= 0 ? availableHints : undefined}
                    animated={isEmpty(ftueOptions) && customArrows.length === 0}
                    disabled={
                        disableByState ||
                        isEmpty(puzzle) ||
                        showSolution ||
                        isInitialized ||
                        disableByMoveList ||
                        (!isEmpty(ftueOptions) &&
                            !(ftueOptions?.highlightHint || ftueOptions?.highlightSecondHint >= 0))
                    }
                    showTooltip={hintFtue || (typeof hint2Ftue === 'number' && hint2Ftue >= 0) || showHintLimit}
                />
            )}
        </Stack>
    )
}

export default PuzzlePracticeControls
